import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { prospectsAPI, configuratorAPI, rentalCalcAPI } from '../utils/api';
import {
  Building2, Users, Mail, Phone, MapPin, Plus, Edit, Trash2,
  ChevronRight, FileText, Zap, Battery, Upload, FolderOpen,
  Fuel, ClipboardList, CheckCircle, AlertTriangle, Link2, Copy, Check,
  Settings2, Play, Loader2, ShieldCheck, BarChart3, Info,
  Calculator, Send, Lock, ArrowLeft, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  NEW: { label: 'New', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  CONTACTED: { label: 'Contacted', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  DATA_PENDING: { label: 'Data Pending', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  DATA_RECEIVED: { label: 'Data Received', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  CONFIGURED: { label: 'Configured', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  PROPOSAL_SENT: { label: 'Proposal Sent', bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  WON: { label: 'Won', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  LOST: { label: 'Lost', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: ClipboardList },
  { id: 'sites', label: 'Sites', icon: Building2 },
  { id: 'files', label: 'Files', icon: FolderOpen },
];

const emptySiteForm = { site_name: '', address: '', city: '', state: '', pincode: '', has_dg: false, notes: '' };
const emptyReqForm = { use_case: 'backup', backup_hours_required: '', critical_load_kw: '', expected_energy_kwh_per_day: '', has_solar: false, constraints: '' };
const emptyDGForm = { dg_capacity_kva: '', dg_make: '', dg_model: '', dg_age_years: '', dg_fuel_type: 'diesel', dg_notes: '' };

export default function ProspectDetailPage() {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const [prospect, setProspect] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showAddSite, setShowAddSite] = useState(false);
  const [showAddReq, setShowAddReq] = useState(false);
  const [showAddDG, setShowAddDG] = useState(false);
  const [activeSiteId, setActiveSiteId] = useState(null);
  const [siteForm, setSiteForm] = useState(emptySiteForm);
  const [reqForm, setReqForm] = useState(emptyReqForm);
  const [dgForm, setDGForm] = useState(emptyDGForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        prospectsAPI.getById(prospectId),
        prospectsAPI.getSites(prospectId),
      ]);
      setProspect(pRes.data);
      setSites(sRes.data);
    } catch (e) { toast.error('Failed to load prospect'); }
    finally { setLoading(false); }
  }, [prospectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setTab = (t) => setSearchParams({ tab: t });

  const handleStatusChange = async (status) => {
    try {
      await prospectsAPI.updateStatus(prospectId, status);
      toast.success(`Status updated to ${STATUS_CONFIG[status]?.label}`);
      fetchData();
    } catch (e) { toast.error('Failed to update status'); }
  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await prospectsAPI.createSite(prospectId, { ...siteForm, prospect_id: prospectId });
      toast.success('Site added');
      setShowAddSite(false);
      setSiteForm(emptySiteForm);
      fetchData();
    } catch (e) { toast.error('Failed to add site'); }
    finally { setSubmitting(false); }
  };

  const handleAddReq = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await prospectsAPI.createRequirement(activeSiteId, {
        prospect_site_id: activeSiteId,
        ...reqForm,
        backup_hours_required: reqForm.backup_hours_required ? Number(reqForm.backup_hours_required) : null,
        critical_load_kw: reqForm.critical_load_kw ? Number(reqForm.critical_load_kw) : null,
        expected_energy_kwh_per_day: reqForm.expected_energy_kwh_per_day ? Number(reqForm.expected_energy_kwh_per_day) : null,
      });
      toast.success('Requirement added');
      setShowAddReq(false);
      setReqForm(emptyReqForm);
      fetchData();
    } catch (e) { toast.error('Failed to add requirement'); }
    finally { setSubmitting(false); }
  };

  const handleAddDG = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await prospectsAPI.createDGAsset(activeSiteId, {
        prospect_site_id: activeSiteId,
        ...dgForm,
        dg_capacity_kva: dgForm.dg_capacity_kva ? Number(dgForm.dg_capacity_kva) : null,
        dg_age_years: dgForm.dg_age_years ? Number(dgForm.dg_age_years) : null,
      });
      toast.success('DG asset added');
      setShowAddDG(false);
      setDGForm(emptyDGForm);
      fetchData();
    } catch (e) { toast.error('Failed to add DG asset'); }
    finally { setSubmitting(false); }
  };

  const handleFileUpload = async (siteId, file, fileType) => {
    try {
      await prospectsAPI.uploadFile(siteId, file, fileType);
      toast.success('File uploaded');
      fetchData();
    } catch (e) { toast.error('Upload failed'); }
  };

  const handleGenerateLink = async (siteId) => {
    try {
      const res = await prospectsAPI.generateIntakeLink(siteId);
      const token = res.data.token;
      const url = `${window.location.origin}/intake/${token}`;
      navigator.clipboard.writeText(url);
      toast.success('Intake link copied to clipboard');
    } catch (e) { toast.error('Failed to generate link'); }
  };

  if (loading) return <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>;
  if (!prospect) return <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 p-8"><p className="text-slate-500">Prospect not found</p></div></div>;

  const breadcrumbs = [
    { label: 'Prospects', to: '/prospects' },
    { label: prospect.company_name },
  ];

  const statusCfg = STATUS_CONFIG[prospect.status] || STATUS_CONFIG.NEW;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Breadcrumbs items={breadcrumbs} onBack={() => navigate('/prospects')} />

          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm mb-6" data-testid="prospect-header">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-indigo-50 border border-indigo-200 rounded-sm flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-slate-900">{prospect.company_name}</h1>
                  <p className="text-sm text-slate-500">{prospect.industry}{prospect.city ? ` · ${prospect.city}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Select value={prospect.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className={`w-[160px] text-xs font-semibold ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`} data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem icon={Users} label="Contact" value={prospect.contact_name} />
              <InfoItem icon={Mail} label="Email" value={prospect.email} />
              <InfoItem icon={Phone} label="Phone" value={prospect.phone || 'N/A'} />
              <InfoItem icon={Building2} label="Sites" value={`${prospect.site_count || 0} site(s)`} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-white border border-slate-200 rounded-sm p-1 mb-6" data-testid="prospect-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setTab(tab.id)} data-testid={`tab-${tab.id}`}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <Icon className="w-4 h-4" /><span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && <OverviewTab prospect={prospect} sites={sites} />}
          {activeTab === 'sites' && (
            <SitesTab sites={sites} onAddSite={() => setShowAddSite(true)}
              onAddReq={(sid) => { setActiveSiteId(sid); setShowAddReq(true); }}
              onAddDG={(sid) => { setActiveSiteId(sid); setShowAddDG(true); }}
              onFileUpload={handleFileUpload}
              onGenerateLink={handleGenerateLink}
              navigate={navigate} />
          )}
          {activeTab === 'files' && <FilesTab sites={sites} onFileUpload={handleFileUpload} />}
        </div>
      </div>

      {/* Add Site Dialog */}
      <Dialog open={showAddSite} onOpenChange={setShowAddSite}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add Prospect Site</DialogTitle></DialogHeader>
          <form onSubmit={handleAddSite} className="space-y-4 mt-2">
            <div><Label>Site Name *</Label><Input value={siteForm.site_name} onChange={e => setSiteForm({...siteForm, site_name: e.target.value})} required className="mt-1" /></div>
            <div><Label>Address</Label><Input value={siteForm.address} onChange={e => setSiteForm({...siteForm, address: e.target.value})} className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>City</Label><Input value={siteForm.city} onChange={e => setSiteForm({...siteForm, city: e.target.value})} className="mt-1" /></div>
              <div><Label>State</Label><Input value={siteForm.state} onChange={e => setSiteForm({...siteForm, state: e.target.value})} className="mt-1" /></div>
              <div><Label>Pincode</Label><Input value={siteForm.pincode} onChange={e => setSiteForm({...siteForm, pincode: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" checked={siteForm.has_dg} onChange={e => setSiteForm({...siteForm, has_dg: e.target.checked})} id="has_dg" className="rounded" />
              <Label htmlFor="has_dg">Site has DG set</Label>
            </div>
            <div><Label>Notes</Label><Input value={siteForm.notes} onChange={e => setSiteForm({...siteForm, notes: e.target.value})} className="mt-1" /></div>
            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddSite(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Adding...' : 'Add Site'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Requirement Dialog */}
      <Dialog open={showAddReq} onOpenChange={setShowAddReq}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add BESS Requirement</DialogTitle></DialogHeader>
          <form onSubmit={handleAddReq} className="space-y-4 mt-2">
            <div>
              <Label>Use Case *</Label>
              <Select value={reqForm.use_case} onValueChange={v => setReqForm({...reqForm, use_case: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="backup">Backup Power</SelectItem>
                  <SelectItem value="dg_displacement">DG Displacement</SelectItem>
                  <SelectItem value="peak_shaving">Peak Shaving</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Backup Hours</Label><Input type="number" step="0.5" value={reqForm.backup_hours_required} onChange={e => setReqForm({...reqForm, backup_hours_required: e.target.value})} className="mt-1" /></div>
              <div><Label>Critical Load (kW)</Label><Input type="number" value={reqForm.critical_load_kw} onChange={e => setReqForm({...reqForm, critical_load_kw: e.target.value})} className="mt-1" /></div>
            </div>
            <div><Label>Expected Energy (kWh/day)</Label><Input type="number" value={reqForm.expected_energy_kwh_per_day} onChange={e => setReqForm({...reqForm, expected_energy_kwh_per_day: e.target.value})} className="mt-1" /></div>
            <div className="flex items-center space-x-2">
              <input type="checkbox" checked={reqForm.has_solar} onChange={e => setReqForm({...reqForm, has_solar: e.target.checked})} id="has_solar" className="rounded" />
              <Label htmlFor="has_solar">Site has solar installation</Label>
            </div>
            <div><Label>Constraints</Label><Input value={reqForm.constraints} onChange={e => setReqForm({...reqForm, constraints: e.target.value})} placeholder="Space, noise, budget..." className="mt-1" /></div>
            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddReq(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Adding...' : 'Add Requirement'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add DG Dialog */}
      <Dialog open={showAddDG} onOpenChange={setShowAddDG}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add DG Set Details</DialogTitle></DialogHeader>
          <form onSubmit={handleAddDG} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Capacity (kVA)</Label><Input type="number" value={dgForm.dg_capacity_kva} onChange={e => setDGForm({...dgForm, dg_capacity_kva: e.target.value})} className="mt-1" /></div>
              <div><Label>Make</Label><Input value={dgForm.dg_make} onChange={e => setDGForm({...dgForm, dg_make: e.target.value})} placeholder="e.g., Cummins" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Model</Label><Input value={dgForm.dg_model} onChange={e => setDGForm({...dgForm, dg_model: e.target.value})} className="mt-1" /></div>
              <div><Label>Age (years)</Label><Input type="number" value={dgForm.dg_age_years} onChange={e => setDGForm({...dgForm, dg_age_years: e.target.value})} className="mt-1" /></div>
            </div>
            <div>
              <Label>Fuel Type</Label>
              <Select value={dgForm.dg_fuel_type} onValueChange={v => setDGForm({...dgForm, dg_fuel_type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="png">PNG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={dgForm.dg_notes} onChange={e => setDGForm({...dgForm, dg_notes: e.target.value})} className="mt-1" /></div>
            <div className="flex justify-end space-x-3 pt-3 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAddDG(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Adding...' : 'Add DG Set'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center space-x-2">
    <div className="w-8 h-8 bg-slate-50 rounded-sm flex items-center justify-center"><Icon className="w-4 h-4 text-slate-400" /></div>
    <div><p className="text-[10px] text-slate-500 uppercase">{label}</p><p className="text-sm font-medium text-slate-900 truncate">{value}</p></div>
  </div>
);

const OverviewTab = ({ prospect, sites }) => {
  const totalReqs = sites.reduce((s, site) => s + (site.requirements?.length || 0), 0);
  const totalDGs = sites.reduce((s, site) => s + (site.dg_assets?.length || 0), 0);
  const totalFiles = sites.reduce((s, site) => s + (site.file_count || 0), 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <StatCard icon={Building2} title="Prospect Sites" value={sites.length} color="indigo" />
      <StatCard icon={Zap} title="BESS Requirements" value={totalReqs} color="amber" />
      <StatCard icon={Fuel} title="DG Sets" value={totalDGs} color="slate" />
      <StatCard icon={FileText} title="Uploaded Files" value={totalFiles} color="blue" />
      <StatCard icon={Users} title="Owner" value={prospect.owner_name || 'Unassigned'} color="green" />
      <StatCard icon={ClipboardList} title="Notes" value={prospect.notes || 'None'} color="slate" />
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, color = 'slate' }) => {
  const colors = { indigo: 'border-l-indigo-500', amber: 'border-l-amber-500', slate: 'border-l-slate-400', blue: 'border-l-blue-500', green: 'border-l-green-500' };
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${colors[color]} rounded-sm p-4 shadow-sm`}>
      <div className="flex items-center space-x-2 mb-1"><Icon className="w-4 h-4 text-slate-400" /><p className="text-xs text-slate-500">{title}</p></div>
      <p className="text-lg font-bold text-slate-900">{value}</p>
    </div>
  );
};

const SitesTab = ({ sites, onAddSite, onAddReq, onAddDG, onFileUpload, onGenerateLink }) => {
  const navigate = useNavigate();
  return (
  <div className="space-y-4">
    <div className="flex justify-end">
      <Button onClick={onAddSite} size="sm" className="bg-slate-900" data-testid="add-site-btn"><Plus className="w-4 h-4 mr-2" /> Add Site</Button>
    </div>
    {sites.length === 0 ? (
      <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
        <Building2 className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-500">No sites added yet</p>
      </div>
    ) : sites.map(site => {
      const stage = site.pipeline_stage || 'NEW';
      const nextAction = site.next_action || '';
      return (
      <div key={site.prospect_site_id} className="bg-white border border-slate-200 rounded-sm shadow-sm hover:border-indigo-200 transition-colors cursor-pointer"
        data-testid={`prospect-site-${site.prospect_site_id}`}
        onClick={() => navigate(`/prospects/sites/${site.prospect_site_id}`)}>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-sm flex items-center justify-center"><Building2 className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <h4 className="font-semibold text-slate-900">{site.site_name}</h4>
                <p className="text-xs text-slate-500">{[site.address, site.city, site.state].filter(Boolean).join(', ')}{site.has_dg ? ' · Has DG' : ''}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`text-[10px] font-bold px-2 py-1 rounded border ${stage === 'WON' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : stage === 'LOST' ? 'bg-red-50 text-red-600 border-red-200' : stage.includes('CONFIGURATION') ? 'bg-amber-50 text-amber-700 border-amber-200' : stage.includes('PROPOSAL') ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                {stage.replace(/_/g, ' ')}
              </span>
              <ChevronRight className="w-5 h-5 text-slate-300" />
            </div>
          </div>
          {nextAction && (
            <p className="text-xs text-indigo-600 mt-2 ml-13 pl-13"><span className="font-medium">Next:</span> {nextAction}</p>
          )}
        </div>
      </div>
    );})}
  </div>
);
};

const ConfiguratorTab = ({ sites, prospectId, onStatusUpdate }) => {
  const [runs, setRuns] = useState({});
  const [loading, setLoading] = useState({});
  const [runningReqId, setRunningReqId] = useState(null);

  useEffect(() => {
    // Load existing runs for all sites
    const loadRuns = async () => {
      const runsMap = {};
      for (const site of sites) {
        try {
          const res = await configuratorAPI.getRuns({ prospect_site_id: site.prospect_site_id });
          if (res.data.length > 0) {
            runsMap[site.prospect_site_id] = res.data;
          }
        } catch (e) { /* skip */ }
      }
      setRuns(runsMap);
    };
    if (sites.length > 0) loadRuns();
  }, [sites]);

  const handleRun = async (requirementId, siteId) => {
    setRunningReqId(requirementId);
    try {
      const res = await configuratorAPI.run(requirementId);
      toast.success('Configurator run completed');
      // Add run to the state
      setRuns(prev => ({
        ...prev,
        [siteId]: [res.data, ...(prev[siteId] || [])],
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Configurator run failed');
    } finally {
      setRunningReqId(null);
    }
  };

  const handleSelect = async (runId, optionId, siteId) => {
    setLoading(prev => ({ ...prev, [runId]: true }));
    try {
      await configuratorAPI.selectOption(runId, optionId);
      toast.success('Option selected and locked');
      // Refresh runs for this site
      const res = await configuratorAPI.getRuns({ prospect_site_id: siteId });
      setRuns(prev => ({ ...prev, [siteId]: res.data }));
      onStatusUpdate();
    } catch (e) {
      toast.error('Failed to select option');
    } finally {
      setLoading(prev => ({ ...prev, [runId]: false }));
    }
  };

  const allReqs = sites.flatMap(s =>
    (s.requirements || []).map(r => ({ ...r, site_name: s.site_name, prospect_site_id: s.prospect_site_id }))
  );

  if (allReqs.length === 0) {
    return (
      <div className="text-center py-16 bg-white border border-slate-200 rounded-sm" data-testid="configurator-empty">
        <Settings2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-sm text-slate-500 mb-1">No BESS requirements found</p>
        <p className="text-xs text-slate-400">Add requirements on the Sites tab first, then run the configurator here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="configurator-tab">
      {sites.filter(s => s.requirements?.length > 0).map(site => (
        <div key={site.prospect_site_id} className="bg-white border border-slate-200 rounded-sm shadow-sm">
          {/* Site header */}
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-indigo-50 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">{site.site_name}</h4>
                <p className="text-xs text-slate-500">{site.requirements.length} requirement(s) · {(runs[site.prospect_site_id] || []).length} run(s)</p>
              </div>
            </div>
          </div>

          {/* Requirements + Runs */}
          <div className="divide-y divide-slate-100">
            {site.requirements.map(req => {
              const siteRuns = (runs[site.prospect_site_id] || []).filter(r => r.requirement_id === req.requirement_id);
              const latestRun = siteRuns[0];
              const isRunning = runningReqId === req.requirement_id;

              return (
                <div key={req.requirement_id} className="p-5" data-testid={`config-req-${req.requirement_id}`}>
                  {/* Requirement summary */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Zap className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900 capitalize">{req.use_case?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-500">
                          {req.backup_hours_required ? `${req.backup_hours_required}h backup` : ''}
                          {req.critical_load_kw ? ` · ${req.critical_load_kw} kW load` : ''}
                          {req.expected_energy_kwh_per_day ? ` · ${req.expected_energy_kwh_per_day} kWh/day` : ''}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleRun(req.requirement_id, site.prospect_site_id)}
                      disabled={isRunning}
                      className="bg-slate-900"
                      data-testid={`run-config-${req.requirement_id}`}
                    >
                      {isRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                      {isRunning ? 'Running...' : latestRun ? 'Re-Run' : 'Run Configurator'}
                    </Button>
                  </div>

                  {/* Latest run results */}
                  {latestRun && (
                    <ConfiguratorRunResults
                      run={latestRun}
                      siteId={site.prospect_site_id}
                      onSelect={handleSelect}
                      isSelecting={loading[latestRun.configurator_run_id]}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const ConfiguratorRunResults = ({ run, siteId, onSelect, isSelecting }) => {
  const options = run.outputs_json || [];
  const selectedId = run.selected_option_id;
  const inputs = run.inputs_snapshot_json || {};

  const optionColors = {
    Conservative: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
    Balanced: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
    Lean: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
  };

  return (
    <div data-testid={`run-result-${run.configurator_run_id}`}>
      {/* Input summary */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <Info className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-semibold text-slate-600 uppercase">Input Summary</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div><span className="text-slate-500">Bills:</span> <span className="font-medium text-slate-800">{inputs.bills_count || 0}</span></div>
          <div><span className="text-slate-500">Total kWh:</span> <span className="font-medium text-slate-800">{inputs.bills_total_kwh?.toLocaleString() || '—'}</span></div>
          <div><span className="text-slate-500">Max Demand:</span> <span className="font-medium text-slate-800">{inputs.bills_max_demand_kw ? `${inputs.bills_max_demand_kw} kW` : '—'}</span></div>
          <div><span className="text-slate-500">DG Logs:</span> <span className="font-medium text-slate-800">{inputs.dg_logs_count || 0}</span></div>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Run at: {new Date(run.run_at).toLocaleString()}</p>
      </div>

      {/* Options comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map(opt => {
          const colors = optionColors[opt.option_name] || optionColors.Balanced;
          const isSelected = selectedId === opt.option_id;
          const confidence = (opt.confidence_score * 100).toFixed(0);

          return (
            <div
              key={opt.option_id}
              data-testid={`option-${opt.option_name?.toLowerCase()}`}
              className={`border-2 rounded-sm overflow-hidden transition-all ${
                isSelected ? `${colors.border} ring-2 ring-offset-1 ring-slate-900` : `${colors.border}`
              }`}
            >
              {/* Option header */}
              <div className={`${colors.bg} px-4 py-3 border-b ${colors.border} flex items-center justify-between`}>
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-bold ${colors.accent}`}>{opt.option_name}</span>
                  {isSelected && (
                    <span className="inline-flex items-center text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded-sm">
                      <ShieldCheck className="w-3 h-3 mr-1" /> SELECTED
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${colors.badge}`}>
                  {confidence}% conf.
                </span>
              </div>

              {/* Specs */}
              <div className="p-4 space-y-3">
                <SpecRow label="Inverter" value={`${opt.inverter_power_kw} kW`} icon={Zap} />
                <SpecRow label="Battery" value={`${opt.battery_energy_kwh} kWh`} icon={Battery} />
                <SpecRow label="Backup Hours" value={`${opt.recommended_backup_hours}h`} icon={BarChart3} />
                <SpecRow label="Charging Rate" value={`${opt.charging_rate_kw} kW`} icon={Zap} />
                {opt.expected_daily_discharge_kwh && (
                  <SpecRow label="Daily Discharge" value={`${opt.expected_daily_discharge_kwh} kWh`} icon={BarChart3} />
                )}
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400 uppercase mb-1">Operating Mode</p>
                  <p className="text-xs font-medium text-slate-700 capitalize">{opt.operating_mode?.replace(/_/g, ' ')}</p>
                </div>
              </div>

              {/* Select button */}
              <div className="px-4 pb-4">
                {isSelected ? (
                  <div className="w-full text-center text-xs font-semibold text-slate-500 bg-slate-100 py-2 rounded-sm">
                    <CheckCircle className="w-4 h-4 inline mr-1" /> Locked
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="w-full bg-slate-900"
                    onClick={() => onSelect(run.configurator_run_id, opt.option_id, siteId)}
                    disabled={!!selectedId || isSelecting}
                    data-testid={`select-${opt.option_name?.toLowerCase()}`}
                  >
                    {isSelecting ? 'Selecting...' : 'Select This Option'}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Assumptions detail (collapsible) */}
      {options[0]?.assumptions && (
        <details className="mt-4 bg-slate-50 border border-slate-200 rounded-sm">
          <summary className="cursor-pointer px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100">
            View Sizing Assumptions
          </summary>
          <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            {options.map(opt => (
              <div key={opt.option_id} className="text-xs space-y-1">
                <p className="font-semibold text-slate-700 mb-1">{opt.option_name}</p>
                <p className="text-slate-500">Inverter method: <span className="text-slate-800">{opt.assumptions?.inverter_sizing_method}</span></p>
                <p className="text-slate-500">Battery method: <span className="text-slate-800">{opt.assumptions?.battery_sizing_method}</span></p>
                <p className="text-slate-500">Efficiency: <span className="text-slate-800">{(opt.assumptions?.round_trip_efficiency * 100).toFixed(0)}%</span></p>
                <p className="text-slate-500">C-rate: <span className="text-slate-800">{opt.assumptions?.charging_c_rate}C</span></p>
                <p className="text-slate-500">Multiplier: <span className="text-slate-800">{opt.assumptions?.option_multiplier}x</span></p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const SpecRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center space-x-2">
      <Icon className="w-3.5 h-3.5 text-slate-400" />
      <span className="text-xs text-slate-500">{label}</span>
    </div>
    <span className="text-sm font-bold text-slate-900">{value}</span>
  </div>
);


/* ==================== RENTAL CALCULATOR TAB ==================== */

const DEFAULT_INPUTS = {
  asset_cost_ex_gst: '', commissioning_cost_to_voltide: '', decommissioning_cost_to_voltide: '',
  salvage_value_end: '', expected_discharge_kwh_per_month: '', min_committed_kwh_per_month: '',
  sm_cost_to_voltide_monthly: '', gst_rate: '0.18', deposit_basis: 'energy_only',
};
const DEFAULT_LEVERS = {
  contract_term_months: '60', target_irr_annual: '0.14', negotiation_buffer_pct: '0',
  sm_charge_to_client_monthly: '', commissioning_charge_to_client: '',
  decommissioning_charge_to_client: '', refundable_deposit_months: '3',
};

const RentalCalcTab = ({ sites, prospectId }) => {
  const [calcs, setCalcs] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState(sites[0]?.prospect_site_id || '');
  const [showNew, setShowNew] = useState(false);
  const [activeCalcId, setActiveCalcId] = useState(null);

  const loadCalcs = useCallback(async () => {
    try {
      const res = await rentalCalcAPI.list({ prospect_site_id: activeSiteId });
      setCalcs(res.data);
      if (res.data.length > 0) setActiveCalcId(prev => prev || res.data[0].rental_calc_id);
    } catch (e) { console.error('loadCalcs failed', e); }
  }, [activeSiteId]);

  useEffect(() => {
    if (activeSiteId) loadCalcs();
  }, [activeSiteId, loadCalcs]);

  return (
    <div className="space-y-4" data-testid="rental-calc-tab">
      {/* Site selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Label className="text-sm text-slate-600">Site:</Label>
          <select value={activeSiteId} onChange={e => { setActiveSiteId(e.target.value); setActiveCalcId(null); }}
            className="text-sm border border-slate-200 rounded px-3 py-1.5" data-testid="calc-site-select">
            {sites.map(s => <option key={s.prospect_site_id} value={s.prospect_site_id}>{s.site_name}</option>)}
          </select>
          <span className="text-xs text-slate-400">{calcs.length} calculation(s)</span>
        </div>
        <Button size="sm" className="bg-slate-900" onClick={() => setShowNew(true)} data-testid="new-calc-btn">
          <Plus className="w-4 h-4 mr-2" /> New Calculation
        </Button>
      </div>

      {/* Active calculation detail */}
      {activeCalcId ? (
        <RentalCalcDetail calcId={activeCalcId} onRefresh={loadCalcs} />
      ) : calcs.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-sm">
          <Calculator className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500 mb-1">No rental calculations yet</p>
          <p className="text-xs text-slate-400">Create a new calculation to compute per-kWh pricing for this site</p>
        </div>
      ) : null}

      {/* Calculations list (if multiple) */}
      {calcs.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">All Calculations</p>
          <div className="space-y-1">
            {calcs.map(c => (
              <button key={c.rental_calc_id} onClick={() => setActiveCalcId(c.rental_calc_id)}
                className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between transition-colors ${activeCalcId === c.rental_calc_id ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'}`}>
                <span>{c.notes || 'Untitled'} — ₹{c.outputs?.price_per_kwh_ex_gst_final?.toFixed(2) || '—'}/kWh</span>
                <CalcStatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* New calculation dialog */}
      {showNew && <NewCalcDialog siteId={activeSiteId} onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); setActiveCalcId(id); loadCalcs(); }} />}
    </div>
  );
};

const CALC_STATUS = {
  DRAFT: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  SUBMITTED: { label: 'In Review', color: 'bg-amber-50 text-amber-700' },
  APPROVED: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700' },
  LOCKED: { label: 'Locked', color: 'bg-indigo-50 text-indigo-700' },
};

const CalcStatusBadge = ({ status }) => {
  const cfg = CALC_STATUS[status] || CALC_STATUS.DRAFT;
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${cfg.color}`}>{cfg.label}</span>;
};

const RentalCalcDetail = ({ calcId, onRefresh }) => {
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCashflows, setShowCashflows] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const r = await rentalCalcAPI.get(calcId); setCalc(r.data); }
      catch { toast.error('Failed to load calculation'); }
      finally { setLoading(false); }
    };
    load();
  }, [calcId]);

  if (loading) return <LoadingSpinner />;
  if (!calc) return null;

  const { inputs, levers, outputs, status } = calc;
  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
  const pct = (n) => n != null ? `${(Number(n) * 100).toFixed(2)}%` : '—';

  const handleSubmit = async () => {
    try { await rentalCalcAPI.submit(calcId); toast.success('Submitted for approval'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
  };
  const handleApprove = async () => {
    try { await rentalCalcAPI.approve(calcId, 'approve', ''); toast.success('Approved'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };
  const handleReject = async () => {
    try { await rentalCalcAPI.approve(calcId, 'reject', ''); toast.success('Rejected — returned to draft'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleLock = async () => {
    try { await rentalCalcAPI.lock(calcId); toast.success('Calculation locked'); onRefresh(); setCalc((await rentalCalcAPI.get(calcId)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to lock'); }
  };

  return (
    <div className="space-y-4" data-testid={`calc-detail-${calcId}`}>
      {/* Status header */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Calculator className="w-5 h-5 text-slate-400" />
          <div>
            <p className="font-semibold text-slate-900">{calc.notes || 'Rental Calculation'}</p>
            <p className="text-xs text-slate-500">Created {new Date(calc.created_at).toLocaleDateString()}</p>
          </div>
          <CalcStatusBadge status={status} />
        </div>
        <div className="flex items-center space-x-2">
          {status === 'DRAFT' && <Button size="sm" onClick={handleSubmit} className="bg-slate-900" data-testid="submit-calc-btn"><Send className="w-3.5 h-3.5 mr-1.5" /> Submit for Review</Button>}
          {status === 'SUBMITTED' && (
            <>
              <Button size="sm" variant="outline" onClick={handleReject} data-testid="reject-calc-btn">Reject</Button>
              <Button size="sm" onClick={handleApprove} className="bg-emerald-600" data-testid="approve-calc-btn"><CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approve</Button>
            </>
          )}
          {status === 'APPROVED' && <Button size="sm" onClick={handleLock} className="bg-indigo-600" data-testid="lock-calc-btn"><Lock className="w-3.5 h-3.5 mr-1.5" /> Lock Calculation</Button>}
          {status === 'LOCKED' && <span className="text-xs text-indigo-600 font-semibold flex items-center"><Lock className="w-3.5 h-3.5 mr-1" /> Immutable</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* INPUTS PANEL */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <p className="text-xs font-semibold text-slate-600 uppercase">Inputs (Maker)</p>
          </div>
          <div className="p-4 space-y-2 text-xs">
            <KV label="Asset Cost (ex-GST)" value={fmt(inputs.asset_cost_ex_gst)} />
            <KV label="Commissioning to Voltide" value={fmt(inputs.commissioning_cost_to_voltide)} />
            <KV label="Decommissioning to Voltide" value={fmt(inputs.decommissioning_cost_to_voltide)} />
            <KV label="Salvage Value" value={fmt(inputs.salvage_value_end)} />
            <div className="border-t border-slate-100 pt-2 mt-2" />
            <KV label="Expected Discharge" value={`${inputs.expected_discharge_kwh_per_month?.toLocaleString() || 0} kWh/mo`} />
            <KV label="Min Committed" value={`${inputs.min_committed_kwh_per_month?.toLocaleString() || 0} kWh/mo`} />
            <KV label="S&M to Voltide" value={`${fmt(inputs.sm_cost_to_voltide_monthly)}/mo`} />
            <KV label="GST Rate" value={pct(inputs.gst_rate)} />
            <KV label="Deposit Basis" value={inputs.deposit_basis?.replace(/_/g, ' ')} />
          </div>
        </div>

        {/* LEVERS PANEL */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
            <p className="text-xs font-semibold text-amber-700 uppercase">Approver Levers</p>
          </div>
          <div className="p-4 space-y-2 text-xs">
            <KV label="Contract Term" value={`${levers.contract_term_months} months`} />
            <KV label="Target IRR" value={pct(levers.target_irr_annual)} />
            <KV label="Negotiation Buffer" value={pct(levers.negotiation_buffer_pct)} />
            <div className="border-t border-slate-100 pt-2 mt-2" />
            <KV label="S&M to Client" value={`${fmt(levers.sm_charge_to_client_monthly)}/mo`} />
            <KV label="Commissioning to Client" value={fmt(levers.commissioning_charge_to_client)} />
            <KV label="Decommissioning to Client" value={fmt(levers.decommissioning_charge_to_client)} />
            <KV label="Deposit Months" value={`${levers.refundable_deposit_months} months`} />
          </div>
        </div>

        {/* OUTPUTS PANEL */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-200">
            <p className="text-xs font-semibold text-emerald-700 uppercase">Outputs</p>
          </div>
          <div className="p-4 space-y-2">
            <div className="bg-slate-900 rounded-sm p-3 text-center mb-3">
              <p className="text-[10px] text-slate-400 uppercase">Price per kWh (ex-GST)</p>
              <p className="text-2xl font-bold text-white" data-testid="output-price-kwh">₹{outputs.price_per_kwh_ex_gst_final?.toFixed(2)}</p>
              <p className="text-[10px] text-slate-400">incl GST: ₹{outputs.price_per_kwh_incl_gst_final?.toFixed(2)}</p>
            </div>
            <div className="space-y-2 text-xs">
              <KV label="Base Price (pre-buffer)" value={`₹${outputs.price_per_kwh_ex_gst_base?.toFixed(2)}/kWh`} />
              <KV label="Min Monthly Energy (ex-GST)" value={fmt(outputs.min_monthly_energy_invoice_ex_gst)} />
              <KV label="S&M (incl GST)" value={`${fmt(outputs.sm_charge_incl_gst)}/mo`} />
              <KV label="Min Monthly Total (incl GST)" value={fmt(outputs.min_monthly_total_recurring_incl_gst)} highlight />
              <KV label="Security Deposit" value={fmt(outputs.deposit_amount)} />
              <div className="border-t border-slate-100 pt-2 mt-2" />
              <KV label="IRR Achieved (base)" value={outputs.irr_achieved_base != null ? pct(outputs.irr_achieved_base) : '—'} />
              <KV label="IRR Achieved (final)" value={outputs.irr_achieved_final != null ? pct(outputs.irr_achieved_final) : '—'} highlight />
            </div>
            {outputs.data_quality_flags?.length > 0 && (
              <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
                <AlertTriangle className="w-3 h-3 inline mr-1" />{outputs.data_quality_flags.join('; ')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cashflow table */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <button onClick={() => setShowCashflows(!showCashflows)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="toggle-cashflows">
          <span>Monthly Cashflow ({outputs.contract_term_months + 1} months)</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showCashflows ? 'rotate-180' : ''}`} />
        </button>
        {showCashflows && outputs.cashflows && (
          <div className="border-t border-slate-200 max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 sticky top-0">
                <th className="px-4 py-2 text-left font-semibold text-slate-500">Month</th>
                <th className="px-4 py-2 text-right font-semibold text-slate-500">Cashflow (₹)</th>
                <th className="px-4 py-2 text-right font-semibold text-slate-500">Cumulative (₹)</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {outputs.cashflows.map((cf, i) => {
                  const cumulative = outputs.cashflows.slice(0, i + 1).reduce((a, b) => a + b, 0);
                  return (
                    <tr key={`cashflow-${i}`} className={cf < 0 ? 'text-red-600' : ''}>
                      <td className="px-4 py-1.5">{i === 0 ? 'Initial' : `Month ${i}`}</td>
                      <td className="px-4 py-1.5 text-right font-mono">{fmt(cf)}</td>
                      <td className="px-4 py-1.5 text-right font-mono">{fmt(cumulative)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const KV = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium ${highlight ? 'text-emerald-700 font-bold' : 'text-slate-900'}`}>{value}</span>
  </div>
);

const NewCalcDialog = ({ siteId, onClose, onCreated }) => {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [levers, setLevers] = useState(DEFAULT_LEVERS);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoPopData, setAutoPopData] = useState(null);
  const [loadingAutoPop, setLoadingAutoPop] = useState(true);

  // Auto-populate from configurator + pricing matrix on open
  useEffect(() => {
    const loadAutoPopulate = async () => {
      try {
        const res = await rentalCalcAPI.autoPopulate(siteId);
        const data = res.data;
        setAutoPopData(data);
        if (data.config_found && data.suggested_inputs) {
          const si = data.suggested_inputs;
          setInputs(prev => ({
            ...prev,
            asset_cost_ex_gst: si.asset_cost_ex_gst || '',
            commissioning_cost_to_voltide: si.commissioning_cost_to_voltide || '',
            expected_discharge_kwh_per_month: si.expected_discharge_kwh_per_month || '',
            min_committed_kwh_per_month: si.min_committed_kwh_per_month || '',
          }));
          // Auto-set notes from config
          const cfg = data.config_option;
          if (cfg) setNotes(`${cfg.option_name} — ${cfg.pcs_power_kw}kW/${cfg.battery_energy_kwh}kWh`);
        }
      } catch (e) { console.error('loadAutoPopulate failed', e); }
      finally { setLoadingAutoPop(false); }
    };
    loadAutoPopulate();
  }, [siteId]);

  const num = (v) => v ? Number(v) : 0;

  const handlePreview = async () => {
    try {
      const res = await rentalCalcAPI.compute(
        { ...inputs, asset_cost_ex_gst: num(inputs.asset_cost_ex_gst), commissioning_cost_to_voltide: num(inputs.commissioning_cost_to_voltide), decommissioning_cost_to_voltide: num(inputs.decommissioning_cost_to_voltide), salvage_value_end: num(inputs.salvage_value_end), expected_discharge_kwh_per_month: num(inputs.expected_discharge_kwh_per_month), min_committed_kwh_per_month: num(inputs.min_committed_kwh_per_month), sm_cost_to_voltide_monthly: num(inputs.sm_cost_to_voltide_monthly), gst_rate: num(inputs.gst_rate) },
        { ...levers, contract_term_months: num(levers.contract_term_months), target_irr_annual: num(levers.target_irr_annual), negotiation_buffer_pct: num(levers.negotiation_buffer_pct), sm_charge_to_client_monthly: num(levers.sm_charge_to_client_monthly), commissioning_charge_to_client: num(levers.commissioning_charge_to_client), decommissioning_charge_to_client: num(levers.decommissioning_charge_to_client), refundable_deposit_months: num(levers.refundable_deposit_months) }
      );
      setPreview(res.data);
    } catch { toast.error('Compute failed'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await rentalCalcAPI.create({
        prospect_site_id: siteId,
        inputs: { ...inputs, asset_cost_ex_gst: num(inputs.asset_cost_ex_gst), commissioning_cost_to_voltide: num(inputs.commissioning_cost_to_voltide), decommissioning_cost_to_voltide: num(inputs.decommissioning_cost_to_voltide), salvage_value_end: num(inputs.salvage_value_end), expected_discharge_kwh_per_month: num(inputs.expected_discharge_kwh_per_month), min_committed_kwh_per_month: num(inputs.min_committed_kwh_per_month), sm_cost_to_voltide_monthly: num(inputs.sm_cost_to_voltide_monthly), gst_rate: num(inputs.gst_rate) },
        levers: { ...levers, contract_term_months: num(levers.contract_term_months), target_irr_annual: num(levers.target_irr_annual), negotiation_buffer_pct: num(levers.negotiation_buffer_pct), sm_charge_to_client_monthly: num(levers.sm_charge_to_client_monthly), commissioning_charge_to_client: num(levers.commissioning_charge_to_client), decommissioning_charge_to_client: num(levers.decommissioning_charge_to_client), refundable_deposit_months: num(levers.refundable_deposit_months) },
        notes,
      });
      toast.success('Calculation saved');
      onCreated(res.data.rental_calc_id);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const setI = (k, v) => setInputs(p => ({...p, [k]: v}));
  const setL = (k, v) => setLevers(p => ({...p, [k]: v}));

  const fmtInr = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '';

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Rental Calculation</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">

          {/* Auto-populate summary */}
          {loadingAutoPop ? (
            <div className="flex items-center text-xs text-slate-400 py-2"><Loader2 className="w-3 h-3 mr-2 animate-spin" />Loading configurator & pricing data...</div>
          ) : autoPopData?.config_found ? (
            <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3" data-testid="auto-populate-summary">
              <p className="text-xs font-semibold text-indigo-700 mb-1">Auto-populated from Configurator + Pricing Matrix</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-indigo-600">
                {autoPopData.config_option && (
                  <>
                    <span>Config: <b>{autoPopData.config_option.option_name}</b></span>
                    <span>PCS: <b>{autoPopData.config_option.pcs_power_kw} kW</b></span>
                    <span>Battery: <b>{autoPopData.config_option.battery_energy_kwh} kWh</b></span>
                  </>
                )}
                {autoPopData.pricing_found && autoPopData.pricing_entry && (
                  <>
                    <span>SKU: <b>{autoPopData.pricing_entry.pcs_sku} × {autoPopData.pricing_entry.battery_sku}</b></span>
                    {autoPopData.pricing_entry.total_bess_cost_INR && (
                      <span>BESS Cost: <b>{fmtInr(autoPopData.pricing_entry.total_bess_cost_INR)}</b></span>
                    )}
                  </>
                )}
                {!autoPopData.pricing_found && (
                  <span className="text-amber-600">No matching pricing matrix entry found — enter asset cost manually</span>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <p className="text-xs text-slate-500">No configurator data found — run the configurator first or enter values manually</p>
            </div>
          )}

          <div><Label>Label / Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Option 1 — 500kW/723kWh" className="mt-1" data-testid="calc-notes-input" /></div>

          {/* Inputs */}
          <div className="border border-slate-200 rounded-sm p-4">
            <p className="text-xs font-semibold text-slate-600 uppercase mb-3">Cost Inputs</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <FI label="Asset Cost (ex-GST)" value={inputs.asset_cost_ex_gst} onChange={v => setI('asset_cost_ex_gst', v)} tid="input-asset-cost" prefix="₹" hint="Absolute amount in Rupees" />
              <FI label="Commissioning (Voltide)" value={inputs.commissioning_cost_to_voltide} onChange={v => setI('commissioning_cost_to_voltide', v)} prefix="₹" />
              <FI label="Decommissioning (Voltide)" value={inputs.decommissioning_cost_to_voltide} onChange={v => setI('decommissioning_cost_to_voltide', v)} prefix="₹" />
              <FI label="Salvage Value" value={inputs.salvage_value_end} onChange={v => setI('salvage_value_end', v)} prefix="₹" />
              <FI label="Expected Discharge" value={inputs.expected_discharge_kwh_per_month} onChange={v => setI('expected_discharge_kwh_per_month', v)} tid="input-discharge" suffix="kWh/mo" />
              <FI label="Min Committed" value={inputs.min_committed_kwh_per_month} onChange={v => setI('min_committed_kwh_per_month', v)} tid="input-min-kwh" suffix="kWh/mo" />
              <FI label="S&M to Voltide" value={inputs.sm_cost_to_voltide_monthly} onChange={v => setI('sm_cost_to_voltide_monthly', v)} prefix="₹" suffix="/mo" />
              <FI label="GST Rate" value={inputs.gst_rate} onChange={v => setI('gst_rate', v)} hint="e.g. 0.18 = 18%" />
              <div>
                <Label className="text-xs">Deposit Basis</Label>
                <select value={inputs.deposit_basis} onChange={e => setI('deposit_basis', e.target.value)} className="w-full mt-1 text-sm border border-slate-200 rounded px-3 py-2">
                  <option value="energy_only">Energy Only</option>
                  <option value="energy_plus_sm">Energy + S&M</option>
                </select>
              </div>
            </div>
          </div>

          {/* Levers */}
          <div className="border border-amber-200 rounded-sm p-4 bg-amber-50/30">
            <p className="text-xs font-semibold text-amber-700 uppercase mb-3">Approver Levers</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <FI label="Contract Term" value={levers.contract_term_months} onChange={v => setL('contract_term_months', v)} tid="lever-term" suffix="months" />
              <FI label="Target IRR (annual)" value={levers.target_irr_annual} onChange={v => setL('target_irr_annual', v)} tid="lever-irr" hint="e.g. 0.14 = 14%" />
              <FI label="Negotiation Buffer" value={levers.negotiation_buffer_pct} onChange={v => setL('negotiation_buffer_pct', v)} tid="lever-buffer" suffix="%" />
              <FI label="S&M to Client" value={levers.sm_charge_to_client_monthly} onChange={v => setL('sm_charge_to_client_monthly', v)} prefix="₹" suffix="/mo" />
              <FI label="Commissioning (Client)" value={levers.commissioning_charge_to_client} onChange={v => setL('commissioning_charge_to_client', v)} prefix="₹" />
              <FI label="Decommissioning (Client)" value={levers.decommissioning_charge_to_client} onChange={v => setL('decommissioning_charge_to_client', v)} prefix="₹" />
              <FI label="Deposit" value={levers.refundable_deposit_months} onChange={v => setL('refundable_deposit_months', v)} suffix="months" />
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">Preview Output</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><span className="text-slate-500">Price/kWh:</span> <span className="font-bold text-slate-900">₹{preview.price_per_kwh_ex_gst_final?.toFixed(2)}</span></div>
                <div><span className="text-slate-500">Min Monthly:</span> <span className="font-bold">₹{preview.min_monthly_total_recurring_incl_gst?.toLocaleString('en-IN')}</span></div>
                <div><span className="text-slate-500">Deposit:</span> <span className="font-bold">₹{preview.deposit_amount?.toLocaleString('en-IN')}</span></div>
                <div><span className="text-slate-500">IRR:</span> <span className="font-bold">{preview.irr_achieved_final != null ? `${(preview.irr_achieved_final * 100).toFixed(2)}%` : '—'}</span></div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-3 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button variant="outline" onClick={handlePreview} data-testid="preview-calc-btn">Preview</Button>
            <Button className="bg-slate-900" disabled={saving} onClick={handleSave} data-testid="save-calc-btn">{saving ? 'Saving...' : 'Save Draft'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FI = ({ label, value, onChange, tid, prefix, suffix, hint }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="relative mt-1">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">{prefix}</span>}
      <Input type="number" step="any" value={value} onChange={e => onChange(e.target.value)}
        className={`${prefix ? 'pl-7' : ''} ${suffix ? 'pr-14' : ''}`} data-testid={tid} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{suffix}</span>}
    </div>
    {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);


const FilesTab = ({ sites, onFileUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedSite, setSelectedSite] = useState('');
  const [fileType, setFileType] = useState('electricity_bill');
  const [allFiles, setAllFiles] = useState([]);

  useEffect(() => {
    const fetchFiles = async () => {
      const files = [];
      for (const s of sites) {
        try {
          const res = await prospectsAPI.getFiles(s.prospect_site_id);
          res.data.forEach(f => files.push({ ...f, site_name: s.site_name }));
        } catch (e) { /* skip */ }
      }
      setAllFiles(files);
    };
    if (sites.length > 0) fetchFiles();
  }, [sites]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSite) { toast.error('Select a site first'); return; }
    setUploading(true);
    await onFileUpload(selectedSite, file, fileType);
    setUploading(false);
    // Refresh files
    const files = [];
    for (const s of sites) {
      try { const res = await prospectsAPI.getFiles(s.prospect_site_id); res.data.forEach(f => files.push({ ...f, site_name: s.site_name })); } catch (e) { console.error('getFiles failed for site', s.prospect_site_id, e); }
    }
    setAllFiles(files);
  };

  return (
    <div className="space-y-6">
      {/* Upload section */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm">
        <h4 className="font-semibold text-slate-900 mb-4 flex items-center"><Upload className="w-4 h-4 mr-2" /> Upload Document</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Site</Label>
            <Select value={selectedSite} onValueChange={setSelectedSite}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
              <SelectContent>
                {sites.map(s => <SelectItem key={s.prospect_site_id} value={s.prospect_site_id}>{s.site_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>File Type</Label>
            <Select value={fileType} onValueChange={setFileType}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="electricity_bill">Electricity Bill</SelectItem>
                <SelectItem value="dg_log">DG Log</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>File</Label>
            <Input type="file" onChange={handleUpload} disabled={!selectedSite || uploading} className="mt-1 cursor-pointer" data-testid="file-upload-input" />
          </div>
        </div>
      </div>

      {/* Files list */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
        <div className="p-4 border-b border-slate-200"><h4 className="font-semibold text-slate-900">Uploaded Files ({allFiles.length})</h4></div>
        {allFiles.length === 0 ? (
          <div className="p-8 text-center"><FolderOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-500">No files uploaded yet</p></div>
        ) : (
          <div className="divide-y divide-slate-50">
            {allFiles.map(f => (
              <div key={f.upload_id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{f.original_filename}</p>
                    <p className="text-xs text-slate-500">{f.site_name} · {f.file_type?.replace(/_/g, ' ')} · {new Date(f.uploaded_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                  f.parsing_status === 'APPROVED' ? 'bg-green-50 text-green-700 border-green-200' :
                  f.parsing_status === 'NEEDS_REVIEW' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-slate-50 text-slate-500 border-slate-200'
                }`}>{f.parsing_status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
